import React, { useState } from "react"
import { Button, Modal, Descriptions, Tooltip } from "antd"

const MAX_LENGTH = 56

const renderLongValue = (value: any, variant: "long" | "default"): React.ReactNode => {
  if (value === null || value === undefined) return (
    <span
      style={{
        whiteSpace: "normal",
        wordBreak: "keep-all",
      }}
    >—
    </span>)
  if (typeof value === "boolean") return (
    <span
      style={{
        whiteSpace: "normal",
        wordBreak: "keep-all",
      }}
    >{value ? "Yes" : "No"}
    </span>)

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((item, index) => (
        <div
          key={index}
          style={{
            marginBottom: 4,
            whiteSpace: "normal",
            wordBreak: "keep-all",
          }}
        >
          {typeof item === "object" ? renderObject(item, variant) : item.toString()}
        </div>
      ))
    }

    return renderObject(value, variant)
  }

  const stringValue = value.toString()

  if (stringValue.length > MAX_LENGTH) {
    const truncated = stringValue.slice(0, MAX_LENGTH) + "..."

    return (
      <Tooltip
        title={
          <pre
            style={{
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              maxWidth: "100%",
            }}
          >
            {stringValue}
          </pre>
        }
        overlayStyle={{
          maxWidth: "70%",
          zIndex: 9999,
        }}
        mouseEnterDelay={0.2}
      >
        <span style={{ whiteSpace: "nowrap", cursor: "pointer" }}>{truncated}</span>
      </Tooltip>
    )
  }

  return (
    <span
      style={{
        whiteSpace: "normal",
        wordBreak: "keep-all",
      }}
    >{stringValue}
    </span>)
}

const renderValue = (value: any, variant: "long" | "default"): React.ReactNode => {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((item, index) => (
        <div key={index} style={{ marginBottom: 4 }}>
          {typeof item === "object" ? renderObject(item, variant) : item.toString()}
        </div>
      ))
    }

    return renderObject(value, variant)
  }

  return value.toString()
}

const renderObject = (obj: any, variant: "long" | "default") => (
  <Descriptions bordered column={1} size="small">
    {Object.entries(obj).map(([key, value]) => (
      <Descriptions.Item key={key} label={key}>
        {variant === "default" ? renderValue(value, variant) : renderLongValue(value, variant)}
      </Descriptions.Item>
    ))}
  </Descriptions>
)

interface DynamicDataModalProps {
  data: Record<string, any>;
  variant?: "long" | "default";
  title?: string
}

const JsonViewerModal: React.FC<DynamicDataModalProps> = ({ data, variant = "default", title = "Show data" }) => {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button type="primary" onClick={() => setOpen(true)}>
        {title}
      </Button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Info"
        width="80%"
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {renderObject(data, variant)}
      </Modal>
    </>
  )
}

export default JsonViewerModal
